"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import NextImage from "next/image";
import goldLeaf from "@/public/anniversary/goldLeaf.svg";
import { CTAButton } from "@/shadcn/ui/button";
import { PageFuturesLPSchema } from "@/sanity/pages/PageFuturesLPTemplate";

export default function NetworkSection({
  data,
}: {
  data: PageFuturesLPSchema;
}) {
  if (!data.networkTitle && !data.heroStats) return null;

  return (
    <section className="bg-white py-[12rem] lg:py-[17.5rem]">
      <div className="mx-auto w-[min(170rem,100%)] px-[4rem] flex flex-col items-center gap-[6rem]">
        {/* Header */}
        <div className="flex flex-col items-center gap-[3rem] text-center max-w-[78.5rem]">
          {data.networkChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.networkChip}
            </p>
          )}

          {data.networkTitle && (
            <div className="richtext richtext-center prose-p:mt-[2rem] prose-p:max-w-[76.25rem]">
              <PortableText
                value={data.networkTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* Stats bar — 2 cols mobile, 4 cols desktop */}
        {data.heroStats && (
          <div className="border border-black/10 grid grid-cols-2 w-full max-w-[78.5rem] lg:max-w-[78.5rem]">
            {data.heroStats.map((stat, i) => (
              <div
                key={stat._key ?? i}
                className="flex items-center justify-center gap-[0.5rem] lg:gap-[1.5rem] px-[1.5rem] py-[3rem] lg:py-[2.75rem]"
              >
                <NextImage
                  src={goldLeaf}
                  alt=""
                  width={33}
                  height={48}
                  className="flex-shrink-0"
                />
                <div className="flex flex-col items-center text-center gap-[0.5rem]">
                  <p className="mb-0 font-geist font-medium text-[2.5rem] lg:text-[3rem] leading-none text-[#003828] tracking-[-0.15rem]">
                    {stat.value}
                  </p>
                  <p className="mb-0 font-geist font-normal text-[2rem] leading-[1.5] text-black/70 tracking-[-0.1rem]">
                    {stat.label}
                  </p>
                </div>
                <NextImage
                  src={goldLeaf}
                  alt=""
                  width={33}
                  height={48}
                  className="flex-shrink-0 scale-x-[-1]"
                />
              </div>
            ))}
          </div>
        )}

        {/* Quote card */}
        {data.networkQuoteText && (
          <div className="bg-[#f7f7f7] rounded-[0.25rem] px-[5rem] py-[4rem] flex flex-col items-center gap-[3rem] w-full max-w-[78.5rem]">
            <p className="mb-0 font-geist font-medium text-[2.5rem] leading-[1.4] text-black text-center">
              {data.networkQuoteText}
            </p>
            <div className="flex items-center gap-[2rem]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="24"
                viewBox="0 0 32 24"
                fill="none"
                aria-hidden
                className="w-[4rem] h-[3rem] flex-shrink-0"
              >
                <path
                  d="M18.6893 24L18.6893 14.1453C18.6893 6.54 23.664 1.38533 30.6667 -5.54841e-06L31.9933 2.86799C28.7507 4.09066 26.6667 7.71866 26.6667 10.6667L32 10.6667L32 24L18.6893 24ZM1.90735e-06 24L1.32777e-06 14.1453C8.80487e-07 6.54 4.99733 1.384 12 -3.11757e-06L13.328 2.868C10.084 4.09066 8 7.71867 8 10.6667L13.3107 10.6667L13.3107 24L1.90735e-06 24Z"
                  fill="#003828"
                />
              </svg>
              {data.networkQuoteAttribution && (
                <span className="font-mono font-semibold text-[1.75rem] leading-[1.33] text-[#003828] uppercase tracking-[0.05em]">
                  {data.networkQuoteAttribution}
                </span>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        {data.networkCTA && (
          <CTAButton
            data={data.networkCTA}
            className="w-full lg:w-[40.875rem] rounded-[0.25rem] !bg-black !text-white"
          />
        )}
      </div>
    </section>
  );
}
